import { z } from "zod";

/**
 * The company's extra roof measurement fields (Settings → Materials): vents, pipe flashings, rain
 * caps... Each shows on every measurement; catalog items can be counted from one ("item:<key>").
 * `match`: words that find it among an insurance scope's lines (comma separated).
 */
export interface MeasureItemDef {
  key: string;
  name: string;
  unit: "ea" | "ft";
  match: string;
}

export const measureItemDefSchema = z.object({
  key: z.string().regex(/^[a-z0-9_]{1,40}$/),
  name: z.string().trim().min(1).max(60),
  unit: z.enum(["ea", "ft"]),
  match: z.string().trim().max(300).default(""),
});

export const DEFAULT_MEASURE_ITEMS: MeasureItemDef[] = [
  { key: "pipe_flashing", name: "Pipe flashings", unit: "ea", match: "pipe jack, pipe boot, pipe flashing, flashing - pipe" },
  { key: "turbine", name: "Turbine vents", unit: "ea", match: "turbine" },
  { key: "roof_vent", name: "Roof vents", unit: "ea", match: "roof vent, box vent, static vent, louver" },
  { key: "rain_cap", name: "Rain caps", unit: "ea", match: "rain cap" },
  { key: "ridge_vent", name: "Ridge vent", unit: "ft", match: "ridge vent" },
  { key: "power_vent", name: "Power vents", unit: "ea", match: "power attic vent, power vent, attic fan" },
  { key: "skylight", name: "Skylights", unit: "ea", match: "skylight" },
  { key: "satellite", name: "Satellite dishes", unit: "ea", match: "satellite" },
];

/** A key from a name: "Rain caps 4\"" → "rain_caps_4". */
export const measureItemKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 40) || "item";

/** Whether a scope line's description is this item (by its match words, else its name). */
export function lineMatchesItem(description: string, def: MeasureItemDef): boolean {
  const d = description.toLowerCase();
  const words = (def.match || def.name)
    .split(",")
    .map((w) => w.trim().toLowerCase())
    .filter(Boolean);
  if (!words.some((w) => d.includes(w))) return false;
  // A turbine or power vent isn't also a plain roof vent.
  if (def.key === "roof_vent" && /turbine|power|ridge vent/.test(d)) return false;
  return true;
}
